import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { COLORS, TYPOGRAPHY } from "@tecumseh/theme/design";
import React, { FC } from "react";
import { css } from "styled-components";

interface Props extends WidgetProps, HasCategoryContext {}

const enum fields {
    title = "title",
    description = "description",
    image = "image",
    buttonLabel = "buttonLabel",
    buttonLink = "buttonLink",
    buttonType = "buttonType",
    openInNewTab = "openInNewTab",
}

const enum buttonType {
    masterfluxButton = "masterfluxButton",
    tecumsehButton = "tecumsehButton",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
        [fields.image]: string;
        [fields.buttonLabel]: string;
        [fields.buttonLink]: LinkFieldValue;
        [fields.buttonType]: buttonType;
        [fields.openInNewTab]: boolean;
    };
}

export interface CategoryStaticContentStyle {
    container?: InjectableCss;
    imageWrapper?: InjectableCss;
    categoryStaticImage?: LazyImageProps;
    contentWrapper?: InjectableCss;
    staticTitle?: TypographyProps;
    staticDescription?: InjectableCss;
    circleIcon?: InjectableCss;
    bannerLink?: LinkPresentationProps;
    bannerMasterLink?: LinkPresentationProps;
    bannerButton?: InjectableCss;
    hideContainer?: InjectableCss;
    buttonWrapper?: InjectableCss;
}

export const categoryStaticContentStyle: CategoryStaticContentStyle = {
    container: {
        css: css`
            opacity: 1;
            background-color: ${COLORS.background.whiteSaltpan};
            padding: 20px;
            margin-bottom: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        padding: 12px;
                    `,
                    null,
                ])}
        `,
    },
    imageWrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            max-width: 180px;
                        `,
                        null,
                    ],
                    "minMax",
                )}
        `,
    },
    staticTitle: {
        variant: "h5",
        css: css`
            opacity: 1;
            color: ${getColor("secondary")};
            font-family: ${TYPOGRAPHY.fontFamily.dosis};
            font-weight: ${TYPOGRAPHY.weight.mediumbold};
            font-style: normal;
            letter-spacing: ${TYPOGRAPHY.letterSpacing.smaller};
            text-align: left;
            text-transform: uppercase;
        `,
    },
    staticDescription: {
        css: css`
            opacity: 1;
            color: ${COLORS.black};
            font-family: ${TYPOGRAPHY.fontFamily.montserrat};
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.base};
            font-style: normal;
            letter-spacing: ${TYPOGRAPHY.letterSpacing.smallest};
            text-align: left;
            line-height: ${TYPOGRAPHY.lineHeight.mSmallestbase};
        `,
    },
    circleIcon: {
        css: css`
            height: 10px;
            width: 10px;
            border-radius: 50%;
            display: inline-block;
            opacity: 1;
            background-color: ${COLORS.background.whiteSmoke};
            border: 0.25px solid rgba(17, 41, 69, 0.219214);
            box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.638631);
            flex-shrink: 0;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        margin-top: 8px;
                        margin-left: 8px;
                    `,
                    null,
                ])}
        `,
    },
    bannerButton: {
        css: css`
            border-radius: 18px;
            border: 0.25px solid ${COLORS.fuscousGrey};
            opacity: 1;
            background-color: ${COLORS.background.base};
            padding: 3px 10px;
            margin-top: 7px;
            &:hover {
                .circleIcon {
                    background: ${COLORS.yellow};
                    border: 0.25px solid rgba(17, 41, 69, 0.22);
                    box-shadow: inset 0 0 2px 0 rgba(0, 0, 0, 0.64);
                }
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            width: 50%;
                        `,
                        css`
                            width: 50%;
                        `,
                        css`
                            width: 20%;
                        `,
                        null,
                    ],
                    "minMax",
                )}
        `,
    },
    bannerLink: {
        css: css`
            &:focus {
                outline-color: transparent;
                outline-style: none;
                outline-width: unset;
            }
            &:focus-visible {
                outline-color: transparent;
                outline-style: none;
                outline-width: unset;
            }
            > span {
                opacity: 1;
                color: ${COLORS.black} !important;
                font-family: ${TYPOGRAPHY.fontFamily.montserrat};
                font-size: ${TYPOGRAPHY.size.smallest};
                font-weight: ${TYPOGRAPHY.weight.baseBold};
                font-style: normal;
                letter-spacing: ${TYPOGRAPHY.letterSpacing.smallest};
                text-align: left;
            }
        `,
    },
    bannerMasterLink: {
        css: css`
            &:focus {
                outline-color: transparent;
                outline-style: none;
                outline-width: unset;
            }
            &:focus-visible {
                outline-color: transparent;
                outline-style: none;
                outline-width: unset;
            }
            > span {
                opacity: 1;
                color: white !important;
                font-family: ${TYPOGRAPHY.fontFamily.montserrat};
                font-size: ${TYPOGRAPHY.size.smallest};
                font-weight: ${TYPOGRAPHY.weight.baseBold};
                font-style: normal;
                letter-spacing: ${TYPOGRAPHY.letterSpacing.smallest};
                text-align: left;
            }
        `,
    },
    hideContainer: {
        css: css`
            display: none;
        `,
    },
    buttonWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
        `,
    },
};

const styles = categoryStaticContentStyle;

export const TECCategoryStaticContent = ({ fields, category }: Props) => {
    if (!category) {
        return null;
    }

    const { url, title } = useGetLink(fields.buttonLink);

    if (!url) {
        return null;
    }

    return (
        <>
            <StyledWrapper {...(category.properties.isFamilyPage === "true" ? styles.hideContainer : styles.container)}>
                {fields.image && (
                    <StyledWrapper {...styles.imageWrapper}>
                        <LazyImage src={fields.image} {...styles.categoryStaticImage}></LazyImage>
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.contentWrapper}>
                    <Typography as="h1" {...styles.staticTitle}>
                        {fields.title}
                    </Typography>
                    <Typography as="p" {...styles.staticDescription}>
                        {" "}
                        {fields.description}
                    </Typography>
                </StyledWrapper>

                <StyledWrapper>
                    {fields.buttonType === "tecumsehButton" ? (
                        <StyledWrapper className="TecbannerButton" {...styles.buttonWrapper}>
                            <Link target={fields.openInNewTab ? "_blank" : undefined} href={url} {...styles.bannerLink}>
                                {fields.buttonLabel || title || url}
                            </Link>
                            <Typography {...styles.circleIcon} className="circleIcon"></Typography>
                        </StyledWrapper>
                    ) : (
                        <StyledWrapper className="masterfluxBannerLINK" {...styles.buttonWrapper}>
                            <Link
                                target={fields.openInNewTab ? "_blank" : undefined}
                                href={url}
                                {...styles.bannerMasterLink}
                            >
                                {fields.buttonLabel || title || url}
                            </Link>
                            <Typography {...styles.circleIcon} className="circleIcon"></Typography>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withCategory(TECCategoryStaticContent),
    definition: {
        group: "Basic",
        displayName: "TECCategoryStaticContent",
        fieldDefinitions: [
            {
                name: fields.image,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                displayName: "Static Image",
            },
            {
                name: fields.title,
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.description,
                fieldType: "Translatable",
                editorTemplate: "TextField",
                defaultValue: "",
            },
            {
                name: fields.buttonType,
                fieldType: "General",
                editorTemplate: "RadioButtonsField",
                defaultValue: "tecumsehButton",
                options: [
                    {
                        displayName: "tecumsehButton",
                        value: buttonType.tecumsehButton,
                    },
                    {
                        displayName: "masterfluxButton",
                        value: buttonType.masterfluxButton,
                    },
                ],
            },
            {
                fieldType: "Translatable",
                name: fields.buttonLabel,
                editorTemplate: "TextField",
                displayName: "Button Label",
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.buttonLink,
                displayName: "Button Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
            },
            {
                name: fields.openInNewTab,
                displayName: "Open in new tab",
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: false,
            },
        ],
    },
};

export default widgetModule;
